import { ScopeButton } from '@monorepo/shared-lib';
import { Col, Form, Row, Select, Typography } from 'antd';
import { FormDatePicker } from 'components/Custom/datePicker';
import { LabelWrapper } from 'components/Custom/labels';
import { FormSelect } from 'components/Custom/selects';
import { formatDisplayDate } from 'i18n/calendarLabels';
import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { turnDrawerOff } from 'store/app/actions';
import { DataLogStatus } from 'store/app/enums';
import { VatReturnLog } from 'store/vat/models';
import { DataJournal } from 'store/vat/models/vatReportData';

type FieldType = {
  adjustmentPeriod?: string;
  captureDate?: Date;
};

type OtherSubmissions = {
  uuid: string;
  period: string;
};

enum FieldNames {
  ADJUSTMENT_PERIOD = 'adjustmentPeriod',
  CAPTURE_DATE = 'captureDate',
}

interface AdjustmentFormProps {
  selectedRowKeys: DataJournal[];
  addExceptionAction: (
    exceptionActionType: string,
    selectedRowKeys: DataJournal[],
    setSelectedRowKey: (data: DataJournal[]) => void,
    allExceptionsSelected: boolean,
    setAllExceptionsSelected: (val: boolean) => void,
    selectedAdjustmentPeriod: string | null,
    selectedCaptureDate: any
  ) => void;
  selectedVatReturnLogs: VatReturnLog[];
  setSelectedRowKey: (data: DataJournal[]) => void;
  allExceptionsSelected: boolean;
  setAllExceptionsSelected: (val: boolean) => void;
}

//const { Text, Title } = Typography;
export const AdjustmentForm: React.FC<AdjustmentFormProps> = ({
  selectedRowKeys,
  addExceptionAction,
  selectedVatReturnLogs,
  setSelectedRowKey,
  allExceptionsSelected,
  setAllExceptionsSelected,
}) => {
  const [selectedAdjustmentPeriod, setSelectedAdjustmentPeriod] =
    React.useState<string | null>(null);
  const [selectedCaptureDate, setSelectedCaptureDate] = React.useState<any>();
  const [otherSubmissions, setOtherSubmissions] =
    React.useState<OtherSubmissions[]>();

  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const handleSubmit = () => {
    addExceptionAction(
      'ADJUSTMENT',
      selectedRowKeys,
      () => {},
      allExceptionsSelected,
      setAllExceptionsSelected,
      selectedAdjustmentPeriod,
      selectedCaptureDate
    );
    dispatch(turnDrawerOff());
    setSelectedRowKey([]);
  };

  const onAdjustmentPeriodChange = (period) => {
    setSelectedAdjustmentPeriod(period);
  };

  const onCaptureDateChange = (date: any) => {
    form.validateFields([[FieldNames.CAPTURE_DATE]]);

    if (date.isAfter(selectedCaptureDate)) {
      form.resetFields(['returnDueDate']);
    }
    setSelectedCaptureDate(date);
  };

  React.useEffect(() => {
    if (selectedVatReturnLogs) {
      const submittedLogs = selectedVatReturnLogs.filter(
        (vrl) => DataLogStatus[vrl.status] === DataLogStatus.SUBMITTED
      );
      submittedLogs.sort(function (a, b) {
        return (
          new Date(a.fromReportingDate).valueOf() -
          new Date(b.fromReportingDate).valueOf()
        );
      });
      const otherLogs: OtherSubmissions[] = [
        { uuid: 'OOS', period: t('vat-report.out-of-system') },
      ];
      submittedLogs.forEach((sl) => {
        const fromDate = formatDisplayDate(
          sl.fromReportingDate,
          'DD/MM/YYYY',
          i18n.language
        );
        const toDate = formatDisplayDate(
          sl.toReportingDate,
          'DD/MM/YYYY',
          i18n.language
        );
        const period = `${fromDate} - ${toDate}`;
        const uuid = sl.uuid;
        otherLogs.push({ uuid, period });
      });
      setOtherSubmissions(otherLogs);
    }
  }, [i18n.language, selectedVatReturnLogs, t]);
  return (
    <>
      <Typography
        style={{
          fontSize: '12px',
          fontWeight: '400',
          lineHeight: '18px',
          textAlign: 'left',
          color: 'var(--color-text-dark, #042a2a)',
          marginBottom: '40px',
        }}
      >
        {t('vat-report.selected-exceptions-adjusted-prior-period-vat-return')}
        <br />
        <br /> {t('vat-report.after-submitting-review-details-needs-submitted')}
      </Typography>
      <Row justify={'center'}>
        <Col span={24}>
          <Form
            name="basic"
            form={form}
            autoComplete="off"
            onFinish={handleSubmit}
          >
            <LabelWrapper>
              <span className="label-text">
                {t('exceptions.adjustment-period')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="adjustmentPeriod"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (
                      !selectedAdjustmentPeriod &&
                      selectedAdjustmentPeriod !== null
                    ) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormSelect
                placeholder={t('common.select')}
                onChange={onAdjustmentPeriodChange} //@ts-ignore
                width="100%"
                height="45px"
                bordercolor="var(--color-border, #dbeae3)"
                bgcolor="var(--color-surface-raised, #FFFFFF)"
              >
                {otherSubmissions?.map((os) => (
                  <Select.Option key={os.uuid} value={os.uuid}>
                    <span
                      key={os.uuid}
                      //style={editMode ? { color: '#2E2E2E' } : {}}
                    >
                      {os.period}
                    </span>
                  </Select.Option>
                ))}
              </FormSelect>
            </Form.Item>

            <LabelWrapper>
              <span className="label-text">
                {t('exceptions.adjustment-date')}
              </span>
            </LabelWrapper>
            <Form.Item<FieldType>
              name="captureDate"
              rules={[
                {
                  message: t('common.required-field'),
                  validator: () => {
                    if (!selectedCaptureDate) {
                      return Promise.reject('Message problem');
                    } else {
                      return Promise.resolve();
                    }
                  },
                },
              ]}
            >
              <FormDatePicker
                style={{ width: '100%' }}
                placeholder={t('common.select')}
                onChange={onCaptureDateChange}
                format="DD/MM/YYYY" //@ts-ignore
                hasValue={!!selectedCaptureDate}
              />
            </Form.Item>

            <Form.Item>
              <ScopeButton
                type="primary"
                htmlType="submit" //@ts-ignore
                width="100%"
              >
                {t('common.save')}
              </ScopeButton>
            </Form.Item>
          </Form>
        </Col>
      </Row>
    </>
  );
};
